import type { ReactNode } from "react";

// Lucide glyphs (https://lucide.dev), inlined rather than pulled in as a
// package — the editor needs a handful and the design system asks for Lucide.

type IconProps = {
  size?: number;
};

const Glyph = ({
  size = 15,
  children,
}: IconProps & { children: ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const SparklesIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M9.9 3.7a.6.6 0 0 1 1.1 0l1.3 3.4a.6.6 0 0 0 .3.3l3.4 1.3a.6.6 0 0 1 0 1.1l-3.4 1.3a.6.6 0 0 0-.3.3l-1.3 3.4a.6.6 0 0 1-1.1 0l-1.3-3.4a.6.6 0 0 0-.3-.3L4.9 10.8a.6.6 0 0 1 0-1.1l3.4-1.3a.6.6 0 0 0 .3-.3z" />
    <path d="M18 14v4M16 16h4M17 3v3M15.5 4.5h3" />
  </Glyph>
);

export const LayersIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="m12 2 9 5-9 5-9-5z" />
    <path d="m3 12 9 5 9-5M3 17l9 5 9-5" />
  </Glyph>
);

export const TagIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M20.6 13.3 12.7 21a1.7 1.7 0 0 1-2.4 0l-7.6-7.6a1.7 1.7 0 0 1-.5-1.2V4a1.7 1.7 0 0 1 1.7-1.7h8.2c.4 0 .9.2 1.2.5l7.3 7.3a1.7 1.7 0 0 1 0 2.4z" />
    <path d="M7.5 7.5h.01" />
  </Glyph>
);

export const ScrollIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M3 6h18M3 12h18M3 18h12" />
  </Glyph>
);

export const PlusIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M12 5v14M5 12h14" />
  </Glyph>
);

export const PencilIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </Glyph>
);

export const ArchiveIcon = (props: IconProps) => (
  <Glyph {...props}>
    <rect x="3" y="3" width="18" height="5" rx="1" />
    <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
  </Glyph>
);

export const TrashIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M10 11v6M14 11v6" />
  </Glyph>
);

export const RotateIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
  </Glyph>
);

// Points right when closed; the stylesheet rotates it once a row opens.
export const ChevronIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="m9 18 6-6-6-6" />
  </Glyph>
);

// A trait unlocks what a template gates on, so a key reads truer than a badge.
export const KeyIcon = (props: IconProps) => (
  <Glyph {...props}>
    <circle cx="7.5" cy="15.5" r="3.5" />
    <path d="m10 13 8.5-8.5M16 7l2.5 2.5M13.5 9.5 16 12" />
  </Glyph>
);

export const EyeIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
    <circle cx="12" cy="12" r="2.6" />
  </Glyph>
);

export const EyeOffIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M10.6 6.2A9.9 9.9 0 0 1 12 6c6.5 0 10 6 10 6a17 17 0 0 1-3.1 3.7M6.2 7.6A17 17 0 0 0 2 12s3.5 6 10 6a9.7 9.7 0 0 0 4-.8" />
    <path d="M3 3l18 18" />
  </Glyph>
);

export const InfoIcon = (props: IconProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="9.5" />
    <path d="M12 16v-5M12 8h.01" />
  </Glyph>
);

export const XIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Glyph>
);
